import { useState, useEffect } from "react";

const EXPERIENCE = [
  {
    company: "Clye GmbH",
    role: "Produktverantwortlicher CLYE AI",
    period: "05/2025 – Heute",
    current: true,
    tasks: [
      "Produktverantwortung & Roadmap für CLYE AI",
      "Strategische KI-Produktentwicklung",
      "Koordination zwischen Entwicklung und Stakeholdern",
    ],
  },
  {
    company: "WEBER GmbH",
    role: "Werkstudent IT",
    period: "03/2023 – 05/2025",
    current: false,
    tasks: [
      "Interne Softwareentwicklung",
      "Software Testing & Qualitätssicherung",
    ],
  },
  {
    company: "Ford Werke GmbH",
    role: "Dualer Student",
    period: "09/2021 – 03/2022",
    current: false,
    tasks: ["Softwareentwicklung", "Datenanalyse mit Splunk"],
  },
];

const SKILLS = [
  {
    label: "Programmiersprachen",
    items: ["C#", "Java", "Python", "C++", "SQL", "Dart"],
  },
  {
    label: "Mobile & Cross-Platform",
    items: ["SwiftUI", "Flutter", "iOS", "Android"],
  },
  {
    label: "Testing & QA",
    items: ["Selenium", "Cypress", "Playwright", "Postman", "ISTQB"],
  },
  {
    label: "DevOps & Tools",
    items: ["Docker", "CI/CD", "Git", "Linux", "Jira", "Confluence"],
  },
  {
    label: "Methoden & Sonstiges",
    items: ["Agile", "Splunk", "Beautiful Soup", "Business-Englisch"],
  },
];

const PROJECTS = [
  {
    name: "CLYE AI",
    url: "https://clye.ai/",
    description: "EU hosted AI platform built by Clye GmbH",
  },
  {
    name: "Container-UI",
    url: "https://github.com/NicolaiKobras/Container-UI",
    description: "Native Swift UI for Apple container System",
  },
  {
    name: "Print Visualizer",
    url: "https://github.com/NicolaiKobras/print_visualizer",
    description:
      "Visualization tool to preview how print quality will be in real life in different sizes",
  },
];

const EDUCATION = [
  {
    institution: "TH Aschaffenburg",
    degree: "Bachelor of Science – Software Design",
    period: "10/2022 – Heute",
    current: true,
  },
  {
    institution: "ISTQB",
    degree: "Certified Tester Foundation Level",
    period: "02/2024",
    cert: true,
  },
  {
    institution: "FOM Hochschule für Oekonomie & Management",
    degree: "Bachelor of Science – Wirtschaftsinformatik",
    period: "09/2021 – 08/2022",
  },
  {
    institution: "Ludwig Geissler Schule",
    degree: "Abitur – Fachschwerpunkt Informatik",
    period: "08/2018 – 07/2021",
  },
];

export function Portfolio() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            observer.unobserve(e.target);
          }
        }),
      { threshold: 0.08, rootMargin: "0px 0px -50px 0px" },
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="portfolio">
      <div className="noise-overlay" aria-hidden="true" />

      {/* Navigation */}
      <nav className={`nav${scrolled ? " nav--scrolled" : ""}${menuOpen ? " nav--open" : ""}`}>
        <a href="#hero" className="nav-logo" onClick={() => setMenuOpen(false)}>NK</a>
        <button
          className={`nav-burger${menuOpen ? " nav-burger--open" : ""}`}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Menü"
          aria-expanded={menuOpen}
        >
          <span /><span /><span />
        </button>
        <div className={`nav-links${menuOpen ? " nav-links--open" : ""}`}>
          <a href="#erfahrung" className="nav-link" onClick={() => setMenuOpen(false)}>Erfahrung</a>
          <a href="#skills" className="nav-link" onClick={() => setMenuOpen(false)}>Skills</a>
          <a href="#projekte" className="nav-link" onClick={() => setMenuOpen(false)}>Projekte</a>
          <a href="#ausbildung" className="nav-link" onClick={() => setMenuOpen(false)}>Ausbildung</a>
          <a href="#kontakt" className="nav-link" onClick={() => setMenuOpen(false)}>Kontakt</a>
          <a href="#blog" className="nav-link" onClick={() => setMenuOpen(false)}>Blog</a>
        </div>
      </nav>

      {/* Hero */}
      <section id="hero" className="hero">
        <div className="hero-glow" aria-hidden="true" />
        <div className="hero-ring hero-ring--1" aria-hidden="true" />
        <div className="hero-ring hero-ring--2" aria-hidden="true" />

        <div className="hero-content">
          <p className="hero-eyebrow">Portfolio · 2026</p>
          <h1 className="hero-name">
            <span className="hero-name-line hero-name-first">Nicolai</span>
            <span className="hero-name-line hero-name-last">Kobras</span>
          </h1>
          <div className="hero-rule" />
          <div className="hero-below">
            <p className="hero-role">Produktverantwortlicher CLYE AI</p>
            <div className="hero-chips">
              <span className="hero-chip">Software Design</span>
              <span className="hero-chip">Product Management</span>
              <span className="hero-chip">Artificial Intelligence</span>
            </div>
          </div>
        </div>

        <div className="hero-scroll">
          <span>Scroll</span>
          <span className="hero-scroll-bar" />
        </div>
      </section>

      {/* Experience */}
      <section id="erfahrung" className="section">
        <div className="container">
          <header className="section-header reveal">
            <span className="section-num">01</span>
            <h2 className="section-title">Erfahrung</h2>
          </header>

          <div className="timeline">
            {EXPERIENCE.map((item, i) => (
              <article
                key={item.company}
                className={`tl-item reveal delay-${i}`}
              >
                <div className="tl-meta">
                  <span className="tl-period">{item.period}</span>
                  {item.current && <span className="tl-badge">Aktuell</span>}
                </div>
                <div className="tl-connector">
                  <div
                    className={`tl-dot${item.current ? " tl-dot--live" : ""}`}
                  />
                  <div className="tl-line" />
                </div>
                <div className="tl-body">
                  <h3 className="tl-company">{item.company}</h3>
                  <p className="tl-role">{item.role}</p>
                  <ul className="tl-tasks">
                    {item.tasks.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Skills */}
      <section id="skills" className="section section--alt">
        <div className="container">
          <header className="section-header reveal">
            <span className="section-num">02</span>
            <h2 className="section-title">Skills</h2>
          </header>

          <div className="skills-grid">
            {SKILLS.map((group, i) => (
              <div
                key={group.label}
                className={`skill-group reveal delay-${i}`}
              >
                <h3 className="skill-group-label">{group.label}</h3>
                <div className="skill-tags">
                  {group.items.map((skill) => (
                    <span key={skill} className="skill-tag">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section id="projekte" className="section">
        <div className="container">
          <header className="section-header reveal">
            <span className="section-num">03</span>
            <h2 className="section-title">Projekte</h2>
          </header>

          <div className="projects-grid">
            {PROJECTS.map((project, i) => (
              <a
                key={project.name}
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`project-card reveal delay-${i}`}
              >
                <h3 className="project-name">{project.name}</h3>
                <p className="project-desc">{project.description}</p>
                <span className="project-arrow">→</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Education */}
      <section id="ausbildung" className="section section--alt">
        <div className="container">
          <header className="section-header reveal">
            <span className="section-num">04</span>
            <h2 className="section-title">Ausbildung</h2>
          </header>

          <div className="edu-list">
            {EDUCATION.map((item, i) => (
              <div
                key={item.institution}
                className={`edu-item reveal delay-${i}`}
              >
                <span className="edu-period">{item.period}</span>
                <div className="edu-info">
                  <h3 className="edu-institution">{item.institution}</h3>
                  <p className="edu-degree">{item.degree}</p>
                </div>
                <div className="edu-badge-col">
                  {item.current && <span className="edu-badge">Laufend</span>}
                  {item.cert && (
                    <span className="edu-badge edu-badge--gold">
                      Zertifikat
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="kontakt" className="section section--alt">
        <div className="container">
          <header className="section-header reveal">
            <span className="section-num">05</span>
            <h2 className="section-title">Kontakt</h2>
          </header>

          <div className="contact reveal">
            <a href="mailto:mail@nicolaikobras.de" className="contact-email">
              mail@nicolaikobras.de
            </a>
            <div className="contact-meta">
              <span>+49 176 24765764</span>
              <span className="contact-dot">·</span>
              <span>Mömbris, Deutschland</span>
              <span className="contact-dot">·</span>
              <a
                href="https://www.linkedin.com/in/nicolai-kobras-780227225/"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-meta-link"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <span>© 2026 Nicolai Kobras</span>
        <span>Software Design · Product Management</span>
        <a href="#impressum" className="footer-link">
          Impressum
        </a>
      </footer>
    </div>
  );
}

export default Portfolio;
